import {getTestProps} from '@benchling/js-test-utils';
import React, {useCallback, useEffect, useState, useRef, useMemo} from 'react';

import {AgentType, ChatType, LLMRole} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ChatSessionQuery} from 'llm/chat-library/__generated/ChatSessionQuery';
import {trackMessageSent} from 'llm/chat-library/ChatAnalytics';
import * as ChatDAO from 'llm/chat-library/ChatDAO';
import {ChatFileUpload} from 'llm/chat-library/ChatLibraryTypes';
import {ChatMessageThread} from 'llm/chat-library/ChatMessageThread';
import {getLatestSystemState} from 'llm/chat-library/ChatStateUtil';
import {getInitialChatData, setInitialChatData} from 'llm/chat-library/InitialChatData';
import {
  ChatEventNode,
  ClientDisplayChatEvent,
  ClientProgressMessage,
  ClientStreamingAssistantMessage,
  DisplayMessageChatEventNode,
  ProgressStatus,
} from 'llm/chat-library/messages/MessageTypes';
import {createPendingUserMessage, createStreamingMessage} from 'llm/chat-library/messages/MessageUtils';
import {
  AgentMode,
  mapAgentModeToAgentType,
  doesAgentModeSupportSources,
  doesAgentModeSupportPublicData,
} from 'llm/chat-library/sidepanel/ChatTypeUtil';
import {LatestUserMessageInfo} from 'llm/ChatScrollTypes';
import {pollDeepResearchMessage} from 'llm/deep-research/DeepResearchDAO';
import {ProgressData} from 'llm/deep-research/messages/DeepResearchProgress';
import {convertProgressDataResponseToProgressData} from 'llm/deep-research/utils';
import notifications from 'notifications';
import * as Perf from 'performance/Perf';
import {RealtimeMessage} from 'realtime/RealtimeChannel';
import useRealtimeChannelSubscription from 'realtime/useRealtimeChannelSubscription';
import {getRealtimeChannelNameForApiId} from 'realtime/util/RealtimeUtil';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import * as logging from 'util/Logging';
import * as net from 'util/Net';

// Constants
const POLLING_TIMEOUT_MINUTES = 30;
const POLLING_TIMEOUT_MS = POLLING_TIMEOUT_MINUTES * 60 * 1000;

export interface DeepResearchMessageThreadControls {
  messages: Array<ClientDisplayChatEvent>;
  progressData: ProgressData | null;
  chatId: string;
  isSubmitting: boolean;
  sendMessage: (
    agentMode: AgentMode,
    messageContent: string,
    options: {publicDataEnabled: boolean; fileUploads: Array<ChatFileUpload>}
  ) => Promise<void>;
  isLoading: boolean;
}

// Helper function to transform API messages to UI messages
function transformMessagesFromProgressData(
  chatSessionQuery: ChatSessionQuery
): Array<DisplayMessageChatEventNode> {
  const {chatSession} = chatSessionQuery;
  if (chatSession.__typename === 'InaccessibleInfoNode') {
    return [];
  }
  const {events} = chatSession.threads[0];
  return events.filter((event) => event.__typename === 'DisplayMessageChatEventNode');
}

// Helper function to determine if the conversation is complete
function isConversationComplete(messages: Array<ChatEventNode>): boolean {
  const lastDisplayMessage = messages.findLast(
    (message) => message.__typename === 'DisplayMessageChatEventNode'
  );
  return lastDisplayMessage == null || lastDisplayMessage.role === LLMRole.ASSISTANT;
}

// Helper function to determine if this is a follow-up scenario
function isFollowUpScenario(messages: Array<ChatEventNode>): boolean {
  // If there are any completed system reports, this indicates follow-up questions
  return messages.some(
    (message) => message.__typename === 'DisplayMessageChatEventNode' && message.role === LLMRole.ASSISTANT
  );
}

// Helper function to determine status and error message
function determineStatusAndError(chatSessionQuery: ChatSessionQuery): {
  status: ProgressStatus;
  errorMessage: string | null;
} {
  if (chatSessionQuery.chatSession.__typename === 'InaccessibleInfoNode') {
    return {status: ProgressStatus.FAILED, errorMessage: 'Inaccessible chat session'};
  }

  const chatSession = chatSessionQuery.chatSession;
  const messages = chatSession.threads[0].events;
  const systemState = getLatestSystemState(messages);

  // Check if conversation is complete based on messages
  const errorMessage = systemState?.error_message;
  if (errorMessage) {
    return {status: ProgressStatus.FAILED, errorMessage};
  }
  if (isConversationComplete(messages)) {
    return {status: ProgressStatus.COMPLETED, errorMessage: null};
  }

  // Check for timeout - but skip timeout error for follow-up questions
  const isTimedOut = new Date(chatSession.modifiedAt) < new Date(Date.now() - POLLING_TIMEOUT_MS);

  if (isTimedOut && !isFollowUpScenario(messages)) {
    return {
      status: ProgressStatus.FAILED,
      errorMessage: 'Deep research is taking longer than expected.',
    };
  }

  return {status: ProgressStatus.IN_PROGRESS, errorMessage: null};
}

/**
 * Insert a single progress message for all tool calls across all messages.
 *
 * TODO: Rendering multiple progress messages is preferable but the design is not yet implemented.
 * Challenge is if DEEP_RESEARCH and ASK messages are interleaved, it's not clear how we want to surface
 * the tool calls. For now, we assume that all tools calls are in service of the last assistant message.
 * Note that with the current implementation of ChatProgressMessageBubble, if the last message is ASK,
 * we will not surface any tool calls.
 */
function maybeInsertProgressMessage(
  messages: Array<ClientDisplayChatEvent>,
  status: ProgressStatus | undefined,
  errorMessage: string | null,
  progressData: ProgressData | null
): Array<ClientDisplayChatEvent> {
  const lastUserMessage = messages.findLast((msg) => msg.role === LLMRole.USER);
  if (!status || !lastUserMessage) {
    return messages;
  }
  const progressMessageIndex = messages.findLastIndex((msg) => msg.__typename === 'ClientProgressMessage');

  const updatedMessages = [...messages];
  const progressMessage: ClientProgressMessage = {
    __typename: 'ClientProgressMessage',
    status,
    errorMessage,
    agentType: lastUserMessage.agentType,
    role: null,
    progressData,
  };

  // Remove the existing progress message
  if (progressMessageIndex !== -1) {
    updatedMessages.splice(progressMessageIndex, 1);
  }
  // Add the new progress message at the end
  updatedMessages.push(progressMessage);
  return updatedMessages;
}

// Helper function to insert or update streaming message in the messages array
function insertStreamingMessage(
  messages: Array<ClientDisplayChatEvent>,
  streamingContent: string,
  responseToEventId: string
): Array<ClientDisplayChatEvent> {
  // First, check if there's already ANY streaming message and update it
  // This ensures we only have one streaming message at a time
  const existingStreamingIndex = messages.findIndex(
    (msg) => msg.__typename === 'ClientStreamingAssistantMessage'
  );

  if (existingStreamingIndex >= 0) {
    // Update the existing streaming message
    const existingMessage = messages[existingStreamingIndex] as ClientStreamingAssistantMessage;
    const updatedStreamingMsg = createStreamingMessage({
      createdAt: existingMessage.createdAt,
      agentType: existingMessage.agentType,
      responseToEventId,
      streamingContent,
    });

    return [
      ...messages.slice(0, existingStreamingIndex),
      updatedStreamingMsg,
      ...messages.slice(existingStreamingIndex + 1),
    ];
  }

  // No existing streaming message, insert a new one
  const userMsgIndex = messages.findIndex(
    (msg) => msg.__typename === 'DisplayMessageChatEventNode' && msg.id === responseToEventId
  );

  const newStreamingMessage = createStreamingMessage({
    createdAt: new Date().toISOString(),
    agentType: AgentType.ASK,
    responseToEventId,
    streamingContent,
  });

  if (userMsgIndex >= 0) {
    // Find next user message or end of array
    let insertIndex = userMsgIndex + 1;

    // Skip past any non-user messages (like progress messages)
    while (insertIndex < messages.length && messages[insertIndex].role !== LLMRole.USER) {
      insertIndex++;
    }

    // Insert new streaming message
    return [...messages.slice(0, insertIndex), newStreamingMessage, ...messages.slice(insertIndex)];
  }

  // If we can't find the user message, append to end
  return [...messages, newStreamingMessage];
}

export function useDeepResearchMessageThread({chatId}: {chatId: string}): DeepResearchMessageThreadControls {
  // If a chat was initiated via another component (e.g. NewChatInput), restore state for optimistic updates.
  // As seen below, we will set the initial messages and isSubmitting. We do not need to reset this state when
  // chatId changes, as `ChatRouter` is rendered with a different `key` when chat id changes.
  const initialChatData = getInitialChatData(chatId);
  const initialProgressData = useMemo(
    () => (initialChatData ? {numSources: 0, historyThread: []} : null),
    [initialChatData]
  );
  const initialMessages = useMemo(
    () =>
      maybeInsertProgressMessage(
        initialChatData
          ? [
              createPendingUserMessage({
                text: initialChatData.message.text,
                fileUploads: initialChatData.message.fileUploads,
                agentType: initialChatData.message.agentType,
              }),
            ]
          : [],
        ProgressStatus.STARTED,
        null,
        initialProgressData
      ),
    [initialChatData, initialProgressData]
  );

  const [messages, setMessages] = useState<Array<ClientDisplayChatEvent>>(initialMessages);
  const [submitCount, setSubmitCount] = useState<number>(0);
  const [progressData, setProgressData] = useState<ProgressData | null>(initialProgressData);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const [isLoading, setIsLoading] = useState(initialMessages.length === 0);

  useEffect(() => {
    if (!initialChatData) {
      return;
    }
    setIsSubmitting(true);

    initialChatData.messagePromise.catch((e: unknown) => {
      if (!net.isJqXHR(e)) {
        throw e;
      }
      // If posting the initial message failed, show a toast and unlock the UI.
      notifications.notifyError(e);
      if (e.status >= 400 && e.status < 500) {
        setInitialChatData(chatId, null);
        setMessages([]);
        setIsSubmitting(false);
        setProgressData(null);
      }
    });
  }, [initialChatData, chatId]);

  const receivedChunks = useRef<Map<number, string>>(new Map());
  const timeToFirstTokenPerfId = useRef<Perf.PerfId | null>(null);

  const handlePollingProgress = useCallback(
    (chatSessionQuery: ChatSessionQuery): boolean => {
      // Transform messages from progress data
      const serverDisplayMessages = transformMessagesFromProgressData(chatSessionQuery);

      // Determine status and error message
      const {status, errorMessage} = determineStatusAndError(chatSessionQuery);

      // Update progress data.
      // TODO: This must be invoked before setMessages is called below. React will re-render after each
      // setState in this callback. If the order were reversed, DeepResearchMessage would log an error due to
      // a progress message existing without progressData. To fix, setMessages and setProgressData could be
      // managed by the same setState hook, or perhaps the progressData could just be stored as metadata in
      // the PROGRESS message.
      const newProgressData = convertProgressDataResponseToProgressData(chatSessionQuery);
      setProgressData(newProgressData);

      // Update messages and replace any non-final messages (streaming or non-persisted user messages) with final ones
      setMessages((prevMessages) => {
        if (serverDisplayMessages.length === 0) {
          return prevMessages;
        }

        const newPersistedMessages = serverDisplayMessages.filter(
          (dm) =>
            !prevMessages.find((pm) => pm.__typename === 'DisplayMessageChatEventNode' && pm.id === dm.id)
        );

        // This is overly simplified and will need to be updated when we add interruptions, but for now,
        // we just assume that any new display messages should cleanly replace the streamed message
        // or any non-persisted user message.
        let nextMessages = newPersistedMessages.length > 0 ? serverDisplayMessages : prevMessages;
        nextMessages = maybeInsertProgressMessage(nextMessages, status, errorMessage, newProgressData);

        return nextMessages;
      });

      if (serverDisplayMessages.length > 0) {
        // Avoid showing the initial messages once we see messages from the server.
        setInitialChatData(chatId, null);
      }

      setIsLoading(false);

      // Clear received chunks when completed
      if (status === ProgressStatus.COMPLETED) {
        receivedChunks.current.clear();
      }

      // Update submitting state
      const shouldContinuePolling = status === ProgressStatus.IN_PROGRESS;
      const shouldContinueSubmitting = shouldContinuePolling || status === ProgressStatus.FAILED;
      setIsSubmitting(shouldContinueSubmitting);

      return shouldContinuePolling;
    },
    [chatId, setProgressData]
  );

  // Poll for the result and set the messages
  useEffect(() => {
    const abortController = new AbortController();
    pollDeepResearchMessage(chatId, abortController.signal, handlePollingProgress).catch((error) => {
      // If the error is from an abort, we can safely ignore it
      if (error?.name !== 'AbortError') {
        throw error;
      }
    });
    return () => {
      abortController.abort();
    };
  }, [
    chatId,
    // "Unused" dependency, but a new value indicates that there will be messages from the server soon,
    // and thus is a signal that we should resume polling.
    submitCount,
    handlePollingProgress,
  ]);

  const onRealtimeMessage = useCallback(
    (message: RealtimeMessage) => {
      if (message.name === 'llm_response_chunk') {
        const data = message.data;

        // Report time-to-first-token metric on first chunk
        if (timeToFirstTokenPerfId.current && data.chunkNumber === 0) {
          Perf.markEnd(timeToFirstTokenPerfId.current);
          Perf.stop(timeToFirstTokenPerfId.current);
          timeToFirstTokenPerfId.current = null;
        }

        receivedChunks.current.set(data.chunkNumber, data.content);

        // Check if we have all chunks in sequence
        let completeContent = '';
        let hasAllChunks = true;
        for (let i = 0; i < receivedChunks.current.size; i++) {
          if (receivedChunks.current.has(i)) {
            completeContent += receivedChunks.current.get(i);
          } else {
            hasAllChunks = false;
            break;
          }
        }

        // Only render if we have all chunks in sequence
        if (hasAllChunks) {
          setMessages((prevMessages) =>
            insertStreamingMessage(prevMessages, `${completeContent}▊`, data.userEventId)
          );
        }
      } else if (message.name === 'llm_response_complete') {
        const data = message.data;

        setMessages((prevMessages) => insertStreamingMessage(prevMessages, data.content, data.userEventId));
        receivedChunks.current.clear();

        // Trigger polling to get the final state after streaming completes
        // Add a small delay to ensure the backend has processed everything
        setTimeout(() => {
          const abortController = new AbortController();
          pollDeepResearchMessage(data.chatId, abortController.signal, handlePollingProgress).catch(
            (error) => {
              if (error?.name !== 'AbortError') {
                logging.error('[llm] Error polling after streaming complete', {extra: {error}});
              }
            }
          );
        }, 250);
      }
    },
    [handlePollingProgress]
  );

  useRealtimeChannelSubscription(getRealtimeChannelNameForApiId(chatId), onRealtimeMessage, 'no_filter');

  const sendMessage = useCallback(
    async (
      agentMode: AgentMode,
      messageContent: string,
      options: {publicDataEnabled: boolean; fileUploads: Array<ChatFileUpload>}
    ) => {
      const trimmedMessage = messageContent.trim();

      const userMessage = createPendingUserMessage({
        text: trimmedMessage,
        fileUploads: options.fileUploads,
        agentType: mapAgentModeToAgentType(agentMode),
      });

      // If sendMessage is invoked outside of a react lifecycle event, then each setState will re-render
      // the component, so to avoid DeepResearchMessageComponent logging an error, we set the progress data
      // before setting the messages.
      const newProgressData = {numSources: 0, historyThread: []};
      setProgressData(newProgressData);
      setMessages((prevMessages) =>
        maybeInsertProgressMessage(
          [...prevMessages, userMessage],
          ProgressStatus.STARTED,
          null,
          newProgressData
        )
      );
      setIsSubmitting(true);

      if (agentMode === AgentMode.ASK) {
        const perfId = Perf.start('chat.ask.ttft_ms', {enabled: false}) ?? '';
        timeToFirstTokenPerfId.current = perfId;
        Perf.markStart(perfId);
      }

      trackMessageSent({
        chatType: ChatType.DEEP_RESEARCH,
        agentType: mapAgentModeToAgentType(agentMode),
        messageCount: messages.filter((m) => m.__typename === 'DisplayMessageChatEventNode').length,
        fileCount: options.fileUploads.length,
        includePublicData: options.publicDataEnabled,
      });

      const message = {text: trimmedMessage, fileUploads: options.fileUploads};
      try {
        const sources = doesAgentModeSupportSources(agentMode)
          ? doesAgentModeSupportPublicData(agentMode) && options.publicDataEnabled
            ? [{type: 'public_data' as const}]
            : []
          : undefined;
        await ChatDAO.appendDisplayMessageToChatSession({
          chatSessionId: chatId,
          agentType: mapAgentModeToAgentType(agentMode),
          messageBlocks: ChatDAO.getMessageBlockInput(message),
          userChatContext: ChatDAO.getUserChatContext({
            contextIds: ChatDAO.getContextIdsFromActiveWorkspace(),
          }),
          sources,
        });
      } catch (e) {
        if (!net.isJqXHR(e)) {
          throw e;
        }
        notifications.notifyError(e);
        if (e.status >= 400 && e.status < 500) {
          setIsSubmitting(false);
          setProgressData(null);
          setMessages((prevMessages) =>
            prevMessages.filter((m) => m.__typename !== 'ClientOptimisticUserMessage')
          );
        }
      }
      setSubmitCount((count) => count + 1);
    },
    [chatId, messages, setProgressData]
  );

  return {
    messages,
    progressData,
    chatId,
    isSubmitting,
    sendMessage,
    isLoading,
  };
}

export interface DeepResearchMessageThreadProps {
  chatSessionId: string;
  isLoading: boolean;
  messages: Array<ClientDisplayChatEvent>;
  progressData: ProgressData | null;
  openActivityModal: (activityData: ProgressData, selectedToolId?: string) => void;
  onUpdateLatestUserMessageInfo?: ((info: LatestUserMessageInfo) => void) | null;
}

export function DeepResearchMessageThread({
  chatSessionId,
  messages,
  progressData,
  openActivityModal,
  onUpdateLatestUserMessageInfo,
  isLoading,
}: DeepResearchMessageThreadProps): JSX.Element {
  if (isLoading && messages.length === 0) {
    return (
      <CenteredHorizontalThrobber
        {...getTestProps({component: 'DeepResearchMessageThread', element: 'loading-throbber'})}
      />
    );
  }
  return (
    <ChatMessageThread
      events={messages}
      progressData={progressData}
      onUpdateLatestUserMessageInfo={onUpdateLatestUserMessageInfo}
      chatSessionId={chatSessionId}
      actionButton={null}
      readonly={false}
    />
  );
}
